import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventsAPI } from '../services/api';
import type { EventUpdate } from '../types';
import { useErrorHandler } from './useErrorHandler';

export const useEvents = (date?: string) => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  const queryKey = date ? ['events', date] : ['events', 'today'];

  const { data: events = [], isLoading, error } = useQuery({
    queryKey,
    queryFn: () => date ? eventsAPI.getByDate(date) : eventsAPI.getToday(),
    retry: 1,
    onError: handleError,
  });

  const createEventMutation = useMutation({
    mutationFn: eventsAPI.create,
    onSuccess: () => {
      // Invalidate and refetch events
      queryClient.invalidateQueries({ queryKey: ['events'] });
      // Also invalidate the specific date query if we have one
      if (date) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
    onError: handleError,
  });

  const updateEventMutation = useMutation({
    mutationFn: ({ eventId, event }: { eventId: string; event: EventUpdate }) =>
      eventsAPI.update(eventId, event),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: handleError,
  });

  const deleteEventMutation = useMutation({
    mutationFn: eventsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: handleError,
  });

  const createFromTodoMutation = useMutation({
    mutationFn: ({ todoId, startTime, durationMinutes }: { 
      todoId: string; 
      startTime: string; 
      durationMinutes?: number 
    }) => eventsAPI.createFromTodo(todoId, startTime, durationMinutes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
    onError: handleError,
  });

  return {
    events,
    isLoading,
    error: error as Error | null,
    createEvent: createEventMutation.mutateAsync,
    updateEvent: updateEventMutation.mutateAsync,
    deleteEvent: deleteEventMutation.mutateAsync,
    createFromTodo: createFromTodoMutation.mutateAsync,
    isCreating: createEventMutation.isLoading,
    isUpdating: updateEventMutation.isLoading,
    isDeleting: deleteEventMutation.isLoading,
  };
};

export const useEventsForTodo = (todoId?: string) => {
  const { data: events = [], isLoading } = useQuery({
    queryKey: ['events', 'todo', todoId],
    queryFn: () => eventsAPI.getByTodoId(todoId!),
    enabled: !!todoId,
  });
  return { events, isLoading };
};